import usePendingState from '../Hooks/usePendingState';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { InputMask } from '@react-input/mask';
import Select from 'react-select';

import { useNotification } from '../Contexts/NotificationContext';
import { useToken } from '../Contexts/TokenContext';
import Loading from './Loading';

const FormEditProfile = () => {
    const { setNotification } = useNotification();
    const [loading, setLoading] = useState(true);
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [profileData, setProfileData] = useState({
        nome: '',
        email: '',
        cnpj: '',
        razao_social: '',
        data_nascimento: '',
        telefone: '',
        pais: '',
        estado: '',
        cidade: '',
        cidade_texto: '',
        password: '',
        password_confirmed: '',
    });

    const [profileErrorData, setProfileErrorData] = useState({
        nome: [],
        email: [],
        cnpj: [],
        razao_social: [],
        data_nascimento: [],
        telefone: [],
        pais: [],
        estado: [],
        cidade: [],
        cidade_texto: [],
        password: [],
        password_confirmed: [],
    });

    const [formLoading, setFormLoading, formPendingRef] = usePendingState();

    const { tokenData } = useToken();
    const navigate = useNavigate();
    const accessToken = localStorage.getItem('access_token');
    const baseUrl = process.env.REACT_APP_API_URL;

    useEffect(() => {
        setProfileData(tokenData);
    }, [tokenData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setProfileData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleCountryChange = (selectedOption) => {
        setProfileData((prevData) => ({
            ...prevData,
            pais: selectedOption.value,
        }));
        setProfileErrorData((prevData) => ({
            ...prevData,
            pais: [],
        }));
    };

    const handleStateChange = async (selectedOption, isFetch = false) => {
        setProfileData((prevData) => ({
            ...prevData,
            estado: selectedOption.value,
            cidade: isFetch ? prevData.cidade : '',
        }));
        setProfileErrorData((prevData) => ({
            ...prevData,
            estado: [],
            cidade: [],
        }));

        try {
            const response = await fetch(`${baseUrl}/painel/estados/${selectedOption.value}/cidades/`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });
            if (response.ok) {
                const data = await response.json();
                setCities(data.cidades);
            } else {
                throw new Error('Erro ao buscar cidades.');
            }
        } catch (error) {
            setNotification({
                type: 'error',
                message: error.message,
                show: true,
            });
        }
    };

    const handleCityChange = (selectedOption) => {
        setProfileData((prevData) => ({
            ...prevData,
            cidade: selectedOption.value,
        }));
        setProfileErrorData((prevData) => ({
            ...prevData,
            cidade: [],
        }));
    };

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const [countriesResponse, statesResponse] = await Promise.all([
                    fetch(`${baseUrl}/painel/paises/`, {
                        method: 'GET',
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            'Content-Type': 'application/json',
                        },
                    }),
                    fetch(`${baseUrl}/painel/estados/`, {
                        method: 'GET',
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            'Content-Type': 'application/json',
                        },
                    }),
                ]);

                if (countriesResponse.ok) {
                    const countriesData = await countriesResponse.json();
                    setCountries(countriesData.paises);
                } else {
                    throw new Error('Erro ao buscar os países.');
                }

                if (statesResponse.ok) {
                    const statesData = await statesResponse.json();
                    setStates(statesData.estados);
                } else {
                    throw new Error('Erro ao buscar os estados.');
                }
            } catch (error) {
                setNotification({
                    type: 'error',
                    message: error.message,
                    show: true,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchContent();
    }, []);

    useEffect(() => {
        if (states.length > 0 && profileData.estado) {
            const selectedOption = states.find((state) => state.value === profileData.estado);
            if (selectedOption) {
                handleStateChange(selectedOption, true);

                setLoading(false);
            }
        }
    }, [states, profileData.estado]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formPendingRef.current) return;
        setFormLoading(true);

        try {
            const response = await fetch(`${baseUrl}/painel/usuario/atualizar/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(profileData),
            });

            if (response.ok) {
                setFormLoading(false);
                navigate('/painel/perfil', {
                    state: {
                        message: { type: 'success', text: 'Cadastro atualizado com sucesso!' },
                    },
                    replace: true,
                });
            } else {
                if (response.status === 422) {
                    const errors = await response.json();
                    setFormLoading(false);
                    setProfileErrorData(errors);
                } else {
                    throw new Error('Erro ao atualizar usuário');
                }
            }
        } catch (error) {
            setNotification({
                type: 'error',
                message: error.message,
                show: true,
            });
        } finally {
            setFormLoading(false);
        }
    };

    if (loading)
        return (
            <div className="mx-auto py-20 flex justify-center">
                <div className="h-16 w-16 animate-spin rounded-full border-4 border-solid border-secondary border-t-transparent"></div>
            </div>
        );

    return (
        <>
            <div className="flex flex-col">
                <form onSubmit={handleSubmit} className="max-w-3xl pb-10">
                    <div className="mb-8 md:mb-10">
                        <div className="mb-3 flex gap-y-3 gap-x-5 lg:gap-5 flex-col lg:flex-row">
                            <div className="w-full lg:w-1/2">
                                <label className="block text-sm text-white font-medium mb-2">Nome</label>
                                <input
                                    type="text"
                                    name="nome"
                                    value={profileData.nome}
                                    onClick={() => setProfileErrorData((prevData) => ({ ...prevData, nome: [] }))}
                                    onChange={handleChange}
                                    className="w-full px-3 py-1.5 2xl:py-2 border border-gray-300 rounded-md outline-none focus:border-secondary focus-visible:shadow-none"
                                />
                                {profileErrorData.nome && profileErrorData.nome[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {profileErrorData.nome[0]}{' '}
                                        <span
                                            onClick={() => setProfileErrorData((prevData) => ({ ...prevData, nome: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>

                            <div className="w-full lg:w-1/2">
                                <label className="block text-sm text-white font-medium mb-2">E-mail</label>
                                <input
                                    type="text"
                                    name="email"
                                    value={profileData.email}
                                    onClick={() => setProfileErrorData((prevData) => ({ ...prevData, email: [] }))}
                                    onChange={handleChange}
                                    className="w-full px-3 py-1.5 2xl:py-2 border border-gray-300 rounded-md outline-none focus:border-secondary focus-visible:shadow-none"
                                />
                                {profileErrorData.email && profileErrorData.email[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {profileErrorData.email[0]}{' '}
                                        <span
                                            onClick={() => setProfileErrorData((prevData) => ({ ...prevData, email: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="mb-3 flex gap-y-3 gap-x-5 lg:gap-5 flex-col lg:flex-row">
                            <div className="w-full lg:w-1/2">
                                <label className="block text-sm text-white font-medium mb-2">Telefone</label>
                                <InputMask
                                    type="text"
                                    name="telefone"
                                    mask="(__) _____-____"
                                    replacement={{ _: /\d/ }}
                                    value={profileData.telefone}
                                    onClick={() => setProfileErrorData((prevData) => ({ ...prevData, telefone: [] }))}
                                    onChange={handleChange}
                                    className="w-full px-3 py-1.5 2xl:py-2 border border-gray-300 rounded-md outline-none focus:border-secondary focus-visible:shadow-none"
                                />
                                {profileErrorData.telefone && profileErrorData.telefone[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {profileErrorData.telefone[0]}{' '}
                                        <span
                                            onClick={() => setProfileErrorData((prevData) => ({ ...prevData, telefone: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>

                            <div className="w-full lg:w-1/2">
                                <label className="block text-sm text-white font-medium mb-2">Data de Nascimento</label>
                                <input
                                    type="date"
                                    name="data_nascimento"
                                    value={profileData.data_nascimento}
                                    onClick={() => setProfileErrorData((prevData) => ({ ...prevData, data_nascimento: [] }))}
                                    onChange={handleChange}
                                    className="w-full px-3 py-1.5 2xl:py-2 border border-gray-300 rounded-md outline-none focus:border-secondary focus-visible:shadow-none"
                                    max={new Date().toJSON().slice(0, 10)}
                                />
                                {profileErrorData.data_nascimento && profileErrorData.data_nascimento[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {profileErrorData.data_nascimento[0]}{' '}
                                        <span
                                            onClick={() => setProfileErrorData((prevData) => ({ ...prevData, data_nascimento: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="mb-3 flex gap-y-3 gap-x-5 lg:gap-5 flex-col lg:flex-row">
                            <div className="w-full lg:w-1/2">
                                <label className="block text-sm text-white font-medium mb-2">CNPJ Representação</label>
                                <InputMask
                                    type="text"
                                    name="cnpj"
                                    mask="__.___.___/____-__"
                                    replacement={{ _: /\d/ }}
                                    value={profileData.cnpj}
                                    onClick={() => setProfileErrorData((prevData) => ({ ...prevData, cnpj: [] }))}
                                    onChange={handleChange}
                                    className="w-full px-3 py-1.5 2xl:py-2 border border-gray-300 rounded-md outline-none focus:border-secondary focus-visible:shadow-none"
                                />
                                {profileErrorData.cnpj && profileErrorData.cnpj[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {profileErrorData.cnpj[0]}{' '}
                                        <span
                                            onClick={() => setProfileErrorData((prevData) => ({ ...prevData, cnpj: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>

                            <div className="w-full lg:w-1/2">
                                <label className="block text-sm text-white font-medium mb-2">Razão Social</label>
                                <input
                                    type="text"
                                    name="razao_social"
                                    value={profileData.razao_social}
                                    onClick={() => setProfileErrorData((prevData) => ({ ...prevData, razao_social: [] }))}
                                    onChange={handleChange}
                                    className="w-full px-3 py-1.5 2xl:py-2 border border-gray-300 rounded-md outline-none focus:border-secondary focus-visible:shadow-none"
                                />
                                {profileErrorData.razao_social && profileErrorData.razao_social[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {profileErrorData.razao_social[0]}{' '}
                                        <span
                                            onClick={() => setProfileErrorData((prevData) => ({ ...prevData, razao_social: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="mb-3 flex gap-y-3 gap-x-5 lg:gap-5 flex-col lg:flex-row">
                            <div
                                className={`transition-all ${profileData.pais === 4 || profileData.pais === '' ? 'lg:w-1/3' : 'lg:w-1/2'}`}
                            >
                                <label className="block text-sm text-white font-medium mb-2">País</label>
                                <Select
                                    name="pais"
                                    options={countries}
                                    value={countries.find((option) => option.value === profileData.pais) || null}
                                    onClick={() => setProfileErrorData((prevData) => ({ ...prevData, pais: [] }))}
                                    onChange={handleCountryChange}
                                    placeholder="Selecione um país..."
                                    className="profile-select"
                                />
                                {profileErrorData.pais && profileErrorData.pais[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {profileErrorData.pais[0]}{' '}
                                        <span
                                            onClick={() => setProfileErrorData((prevData) => ({ ...prevData, pais: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>

                            <div
                                className={`w-full flex gap-5 flex-row transition-all ${profileData.pais === 4 || profileData.pais === '' ? 'lg:w-2/3' : 'lg:w-1/2'}`}
                            >
                                <div
                                    className={`w-full lg:w-1/2 max-h-8 transition-all ${profileData.pais === 4 || profileData.pais === '' ? 'max-lg:max-h-16 lg:max-w-72' : 'max-w-0 overflow-hidden -ml-5'}`}
                                >
                                    <label className="block text-sm text-white font-medium mb-2">Estado</label>
                                    <Select
                                        name="estado"
                                        options={states}
                                        value={states.find((option) => option.value === profileData.estado) || null}
                                        onClick={() => setProfileErrorData((prevData) => ({ ...prevData, estado: [] }))}
                                        onChange={handleStateChange}
                                        placeholder="Selecione um estado..."
                                        className="profile-select"
                                        isDisabled={profileData.pais === ''}
                                    />
                                    {profileErrorData.estado && profileErrorData.estado[0] && (
                                        <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                            {profileErrorData.estado[0]}{' '}
                                            <span
                                                onClick={() => setProfileErrorData((prevData) => ({ ...prevData, estado: [] }))}
                                                className="rotate-45 -my-2 p-2 cursor-pointer"
                                            >
                                                +
                                            </span>
                                        </span>
                                    )}
                                </div>
                                <div
                                    className={`transition-all ${profileData.pais === 4 || profileData.pais === '' ? 'w-full lg:w-1/2' : 'w-full'}`}
                                >
                                    <label className="block text-sm text-white font-medium mb-2">Cidade</label>
                                    {profileData.pais === 4 || profileData.pais === '' ? (
                                        <>
                                            <Select
                                                name="cidade"
                                                options={cities}
                                                value={cities.find((option) => option.value === profileData.cidade) || null}
                                                onClick={() => setProfileErrorData((prevData) => ({ ...prevData, cidade: [] }))}
                                                onChange={handleCityChange}
                                                placeholder={!profileData.estado ? 'Selecione o estado' : 'Selecione uma cidade...'}
                                                className="profile-select"
                                                isDisabled={cities.length === 0}
                                            />

                                            {profileErrorData.cidade && profileErrorData.cidade[0] && (
                                                <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                                    {profileErrorData.cidade[0]}{' '}
                                                    <span
                                                        onClick={() => setProfileErrorData((prevData) => ({ ...prevData, cidade: [] }))}
                                                        className="rotate-45 -my-2 p-2 cursor-pointer"
                                                    >
                                                        +
                                                    </span>
                                                </span>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <input
                                                type="text"
                                                name="cidade_texto"
                                                value={profileData.cidade_texto}
                                                onClick={() => setProfileErrorData((prevData) => ({ ...prevData, cidade_texto: [] }))}
                                                onChange={handleChange}
                                                className="w-full px-3 py-1.5 border border-gray-300 rounded-md outline-none focus:border-secondary focus-visible:shadow-none"
                                            />

                                            {profileErrorData.cidade_texto && profileErrorData.cidade_texto[0] && (
                                                <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                                    {profileErrorData.cidade_texto[0]}{' '}
                                                    <span
                                                        onClick={() =>
                                                            setProfileErrorData((prevData) => ({ ...prevData, cidade_texto: [] }))
                                                        }
                                                        className="rotate-45 -my-2 p-2 cursor-pointer"
                                                    >
                                                        +
                                                    </span>
                                                </span>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="mb-3 flex gap-y-3 gap-x-5 lg:gap-5 flex-row">
                            <div className="w-1/2">
                                <label className="block text-sm text-white font-medium mb-2">Nova Senha</label>
                                <input
                                    type="password"
                                    name="password"
                                    value={profileData.password}
                                    onClick={() => setProfileErrorData((prevData) => ({ ...prevData, password: [] }))}
                                    onChange={handleChange}
                                    className="w-full px-3 py-1.5 2xl:py-2 border border-gray-300 rounded-md outline-none focus:border-secondary focus-visible:shadow-none"
                                />
                                {profileErrorData.password && profileErrorData.password[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {profileErrorData.password[0]}{' '}
                                        <span
                                            onClick={() => setProfileErrorData((prevData) => ({ ...prevData, password: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>

                            <div className="w-1/2">
                                <label className="block text-sm text-white font-medium mb-2">Confirme a senha</label>
                                <input
                                    type="password"
                                    name="password_confirmed"
                                    value={profileData.password_confirmed}
                                    onClick={() => setProfileErrorData((prevData) => ({ ...prevData, password_confirmed: [] }))}
                                    onChange={handleChange}
                                    className="w-full px-3 py-1.5 2xl:py-2 border border-gray-300 rounded-md outline-none focus:border-secondary focus-visible:shadow-none"
                                />
                                {profileErrorData.password_confirmed && profileErrorData.password_confirmed[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {profileErrorData.password_confirmed[0]}{' '}
                                        <span
                                            onClick={() => setProfileErrorData((prevData) => ({ ...prevData, password_confirmed: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={formLoading}
                        aria-busy={formLoading}
                        className="w-full bg-secondary text-white py-2 px-4 rounded-md hover:bg-opacity-80 disabled:bg-opacity-80 disabled:cursor-not-allowed"
                    >
                        {!formLoading ? 'Salvar' : <Loading />}
                    </button>
                </form>
            </div>
        </>
    );
};

export default FormEditProfile;
